import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import { useStoreOwner } from '../store/useStoreOwner'
import { useStoreTotpMode } from '../hooks/useStoreTotpMode'
import { hasTotpFactor } from '../lib/totpMfa'
import { auth } from '../lib/firebase'
import { useI18n } from '../i18n/useI18n'
import StoreTotpManager from './StoreTotpManager'

/**
 * #2663 お店のオーナーの 2 段階認証 (TOTP) の必須化の gate。`STORE_TOTP_MODE=required_all` のときだけ働く。
 *
 * 1. TOTP を登録していない → 登録の画面 (StoreTotpManager) で塞ぐ。登録できたら「ログインし直す」
 *    (登録した直後のトークンにはまだ 2 段階目が無く、API が 403 を返すため)
 * 2. 登録済みだが、このログインで TOTP を通していない (必須にする前からログインしたまま) → 「ログインし直す」
 * 3. TOTP を通したログイン → そのまま。ログインが続いている間は二度と求めない (トークンの更新で印は残る)
 *
 * API の側も同じ判定で 403 `totp_required` を返す (middleware/verify_token.py)。画面だけだと直接叩けば通るため。
 * スタッフ (Firebase の利用者を持たない) は RequireStoreAuth が先に通すので、ここには来ない。
 */
export default function StoreTotpRequiredGate({ children }: { children: React.ReactNode }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { firebaseUser, storeLogout } = useStoreOwner()
  const mode = useStoreTotpMode()
  const user = firebaseUser ?? auth.currentUser
  const enrolled = hasTotpFactor(user)
  // null = まだ見ていない。取れなければ通す (API が止める)
  const [viaTotp, setViaTotp] = useState<{ uid: string; value: boolean } | null>(null)
  const [justEnrolled, setJustEnrolled] = useState(false)

  useEffect(() => {
    if (mode !== 'required_all' || !user || !enrolled) return
    const uid = user.uid
    let cancelled = false
    user
      .getIdTokenResult()
      .then((r) => {
        if (!cancelled) setViaTotp({ uid, value: r.signInSecondFactor === 'totp' })
      })
      .catch(() => {
        if (!cancelled) setViaTotp({ uid, value: true })
      })
    return () => {
      cancelled = true
    }
  }, [mode, user, enrolled])

  if (mode !== 'required_all' || !user) {
    if (mode === null && user) return <Spinner />
    return <>{children}</>
  }

  const relogin = async () => {
    await storeLogout()
    navigate('/store/login', { replace: true })
  }

  if (!enrolled || justEnrolled) {
    return (
      <Screen title={t('storeSettings.totpRequiredTitle')} body={t('storeSettings.totpRequiredBody')}>
        {justEnrolled ? (
          <div className="bg-white rounded-2xl shadow-card p-5">
            <p className="font-body text-sm text-text-primary mb-4 leading-relaxed">{t('storeSettings.totpEnrolledRelogin')}</p>
            <button onClick={relogin} className="w-full h-12 rounded-xl bg-navy text-white font-display font-bold text-sm tap-effect">
              {t('storeSettings.totpReloginButton')}
            </button>
          </div>
        ) : (
          <>
            <StoreTotpManager required onEnrolled={() => setJustEnrolled(true)} />
            {/* 登録をやめて出る人のため。登録が終わったら上の「ログインし直す」だけにする (同じ動きのボタンを 2 つ並べない) */}
            <button onClick={relogin} className="mt-4 w-full h-11 rounded-xl border border-gray-200 font-body text-sm text-text-secondary tap-effect">
              {t('storeSettings.totpRequiredLogout')}
            </button>
          </>
        )}
      </Screen>
    )
  }

  if (!viaTotp || viaTotp.uid !== user.uid) return <Spinner />

  if (!viaTotp.value) {
    return (
      <Screen title={t('storeSettings.totpReloginTitle')} body={t('storeSettings.totpReloginBody')}>
        <button onClick={relogin} className="w-full h-12 rounded-xl bg-navy text-white font-display font-bold text-sm tap-effect">
          {t('storeSettings.totpReloginButton')}
        </button>
      </Screen>
    )
  }

  return <>{children}</>
}

function Spinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-white">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
    </div>
  )
}

function Screen({ title, body, children }: { title: string; body: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-warm-white safe-top safe-bottom">
      <div className="mobile-container px-5 py-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-yokoso-red/10 shrink-0">
            <ShieldCheck size={20} className="text-yokoso-red" />
          </div>
          <h1 className="font-display text-lg font-bold text-text-primary">{title}</h1>
        </div>
        <p className="font-body text-sm text-text-secondary leading-relaxed mb-5">{body}</p>
        {children}
      </div>
    </div>
  )
}
